import { type ReactNode } from "react";

import clsx from "clsx";
import { motion } from "framer-motion";
import { twMerge } from "tailwind-merge";

import { NavLink, sidebarItemClasses } from "./nav-link";
import { sidebarIconClasses } from "./sidebar-item";

interface Props {
  readonly isHidden?: boolean;
  readonly icon: ReactNode;
  readonly expanded: boolean;
  readonly useDevelopmentAnalyticsData: boolean;
  readonly children: ReactNode;
  readonly to: string;
  readonly title: string;
}

export const SidebarItemWithSubitems = ({
  isHidden,
  icon,
  expanded,
  children,
  title,
  useDevelopmentAnalyticsData,
  to,
}: Props) => {
  if (isHidden) {
    return null;
  }

  return (
    <div>
      <NavLink
        to={to}
        className={clsx(sidebarItemClasses, "pointer-events-none")}
      >
        {({ isActive }) => (
          <div className="flex flex-row items-center">
            <div className="relative">
              <span
                className={sidebarIconClasses(
                  useDevelopmentAnalyticsData,
                  isActive,
                )}
              >
                {icon}
              </span>
            </div>
            <motion.span
              className={clsx("ml-2", {
                "pointer-events-none hidden": !expanded,
              })}
              animate={{ opacity: expanded ? 1 : 0 }}
              transition={{ duration: 0.1 }}
            >
              {title}
            </motion.span>
          </div>
        )}
      </NavLink>
      <div className="ml-10 space-y-2">{children}</div>
    </div>
  );
};

export const SidebarSubitem = ({
  children,
  to,
  expanded,
  useDevelopmentAnalyticsData,
  isHidden,
}: {
  children: ReactNode;
  to: string;
  expanded: boolean;
  useDevelopmentAnalyticsData: boolean;
  isHidden?: boolean;
}) => {
  if (isHidden) {
    return null;
  }

  return (
    <NavLink to={to} className={sidebarItemClasses}>
      {({ isActive }) => (
        <div className="flex flex-row items-center text-xs">
          <div className="relative">
            <span
              className={clsx(
                twMerge(
                  sidebarIconClasses(useDevelopmentAnalyticsData, isActive),
                  "w-6 h-6",
                ),
                { "pointer-events-none hidden": !expanded },
              )}
            >
              <i className="fa-regular fa-sharp fa-arrow-turn-down-right text-xxs" />
            </span>
          </div>
          <motion.span
            className={clsx("ml-1.5", {
              "pointer-events-none hidden": !expanded,
            })}
            animate={{ opacity: expanded ? 1 : 0 }}
            transition={{ duration: 0.1 }}
          >
            {children}
          </motion.span>
        </div>
      )}
    </NavLink>
  );
};
